import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { FormulaCard } from '@design-system/Cards/FormulaCard';
import { SHOP_PATH } from '@/constants/paths';
import { useManageSubscriptionLink } from '@/hooks/useManageSubscriptionLink';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { useNutritionViewModal } from '@/features/NutritionsViewModels/NutritionProducts.view.model';
import { Render } from '@/components/Helpers/ComponentRender/Render';
import { getMyIngredientsPageUrl } from '@/utils/nutrition/getMyIngredientsPageUrl';
import { FoodProduct } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { useBootup } from '@/hooks/useBootup';
import { NutritionPageCommonSkeleton } from '../../NutritionPage/NutritionPageCommonSkeleton';
import { cn } from '@/utils/styles';
import { EventTracker } from '@/utils';
import { LatestResultsBanner } from '@/components/shared/LatestResultsBanner';
import { TriSynbioticTaskCard } from '@screens/HomePage/TriSynbiotic/TriSynbioticTaskCard';
import { useShowFormulaRecs } from '@/hooks/useShowFormulaRecs';

export const HomePageFormulaSection = () => {
  const { foodFormulaData, isFoodFormulaLoading, isErrorInFormula } =
    useNutritionViewModal();
  const { bootupState } = useBootup();
  const manageSubscriptionLink = useManageSubscriptionLink();
  const showFormulaRecs = useShowFormulaRecs();
  const kitType = bootupState?.kitType;
  const isFormulasSubscribed = foodFormulaData?.some(
    (item) => item.is_subscribed && !item.is_addon,
  );
  const hasAddOn = foodFormulaData?.some((item) => item.is_addon);

  if (isErrorInFormula) {
    return;
  }

  const trackProductRedirection = (product: FoodProduct) => {
    EventTracker.trackEvent(
      'home',
      `home_myFormulas_${product.name.toLocaleLowerCase()}`,
    );
  };

  const trackOrderFormulasRedirection = () => {
    EventTracker.trackEvent('home', 'home_myFormulas_orderFormulas');
  };

  const trackManageFormulasRedirection = () => {
    EventTracker.trackEvent('home', 'home_preRR_formulas_manageSubscription');
  };

  const getDescriptionBasedOnKitType = (kitType: string) => {
    switch (kitType) {
      case 'OHI':
        return 'Accelerate your path to optimal oral health with ingredients that support your oral microbiome and target scores that need improvement.';
      case 'GI':
        return 'Accelerate your path to optimal gut health with supplements and biotics that fill nutrient gaps and target scores that need improvement.';
      case 'FBI':
        return 'Accelerate your path to better health with supplements and biotics that fill nutrient gaps and target scores that need improvement.';
      default:
        return 'Accelerate your path to better health with supplements and biotics that fill nutrient gaps and target scores that need improvement.';
    }
  };

  if (isFoodFormulaLoading) {
    return (
      <div className="page-container">
        <div className="flex flex-col gap-4 py-6">
          <NutritionPageCommonSkeleton className="h-52 max-w-xl w-[10.5rem]" />
        </div>
      </div>
    );
  }

  if (isErrorInFormula || !foodFormulaData || foodFormulaData.length === 0) {
    return null;
  }

  return (
    <Render when={foodFormulaData && foodFormulaData?.length > 0}>
      <div className="page-container flex flex-col" data-testid="home-formulaSection">
        <LatestResultsBanner className="mb-6" />
        <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2" data-testid="home-formulaSection-title">
          {bootupState?.isEligibleForSupplements
            ? 'My Formulas'
            : 'My Supplements & Biotics'}
        </p>

        <p className="mb-6 typography-body text-tertiary lg:w-[35rem]" data-testid="home-formulaSection-description">
          {getDescriptionBasedOnKitType(kitType ?? 'FBI')}
        </p>

        <div
          className={cn(
            'w-full grid grid-cols-2 gap-2',
            'lg:grid lg:grid-cols-4 lg:gap-2',
            'lg:pb-4',
          )}
          data-testid="home-formulaSection-grid"
        >
          {foodFormulaData?.filter((product) => !product.is_addon).map((product) => (
            <Link
              key={product.id}
              variant="flat"
              to={getMyIngredientsPageUrl({ product: product.name })}
              onClick={() => trackProductRedirection(product)}
              className="w-full"
              data-testid={`home-formulaSection-productLink-${product.name.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <FormulaCard
                title={product.name}
                formulaSrc={product.image_url}
                isSubscribed={product.is_subscribed}
                ingredientsCount={product.ingredients_count}
                isCustom={!bootupState?.isEligibleForSupplements}
                showFormulaRecs={showFormulaRecs}
                className="w-full h-full"
                data-testid={`home-formulaSection-card-${product.name.replace(/\s+/g, '-').toLowerCase()}`}
                isAddOn={product.is_addon}
              />
            </Link>
          ))}
        </div>

        {bootupState?.isEligibleForSupplements && showFormulaRecs &&
          (isFormulasSubscribed ? (
            <Button
              asChild
              variant="secondary"
              className="w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
              data-testid="home-formulaSection-manageSubscriptionButton"
            >
              <Link to={manageSubscriptionLink} onClick={trackManageFormulasRedirection}>
                Manage my subscription
                <ChevronRightIcon height={24} width={24} />
              </Link>
            </Button>
          ) : (
            <Button
              asChild
              variant="outline"
              className="dark bg-overlay-black w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
              data-testid="home-formulaSection-orderFormulasButton"
            >
              <Link to={SHOP_PATH} onClick={trackOrderFormulasRedirection}>
                Order my formulas
                <ChevronRightIcon height={24} width={24} />
              </Link>
            </Button>
          ))
        }

        {hasAddOn && (
          <TriSynbioticTaskCard variant="formulas" />
        )}
      </div>
    </Render>
  );
};
